import React from "react";

import { WatchlistSummaryAccount } from "../../../../Types/WatchlistSummary";
import {
  extractOverallModalAudio,
  extractFinancialModalAudio,
  extractNewsModalAudio,
  extractSocialModalAudio,
  extractTrafficModalAudio,
  extractGrowthModalAudio,
  extractLegalModalAudio,
  extractAboutModalAudio,
  extractSectionAudio,
} from "../../../../Utils/WatchlistSummary/audio";
import { AboutSection } from "../../sections/AboutSection";
import { FinancialSection } from "../../sections/FinancialSection";
import { GrowthSection } from "../../sections/GrowthSection";
import { LeadershipSection } from "../../sections/LeadershipSection";
import { LegalSection } from "../../sections/LegalSection";
import { NewsSection } from "../../sections/NewsSection";
import { OverallSection } from "../../sections/OverallSection";
import { SocialSection } from "../../sections/SocialSection";
import { StackSection } from "../../sections/StackSection";
import { SummarySection } from "../../sections/SummarySection";
import { TrafficSection } from "../../sections/TrafficSection";
import { AudioAgentModal } from "../AudioAgentModal";
import "./style.scss";

interface SectionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountData: WatchlistSummaryAccount | null;
  sectionId: string | null;
  organizationName?: string;
  // Navigation props
  allAccounts?: WatchlistSummaryAccount[];
  allSections?: string[];
  currentAccountIndex?: number;
  currentSectionIndex?: number;
  onNavigate?: (accountIndex: number, sectionIndex: number) => void;
}

export const SectionDetailModal: React.FC<SectionDetailModalProps> = ({
  isOpen,
  onClose,
  accountData,
  sectionId,
  organizationName,
  allAccounts = [],
  allSections = [],
  currentAccountIndex = 0,
  currentSectionIndex = 0,
  onNavigate,
}) => {
  // All hooks must be called before any conditional returns
  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [audioModalContent, setAudioModalContent] = React.useState<{
    title: string;
    content: string;
  } | null>(null);

  // Close audio modal when main modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsAudioModalOpen(false);
      setAudioModalContent(null);
    }
  }, [isOpen]);

  // Navigation handler function (defined before useEffect to avoid dependency issues)
  const handleNavigation = React.useCallback(
    (direction: "left" | "right" | "up" | "down") => {
      if (!onNavigate || !accountData || !sectionId) return;

      const canNavigateLeft = currentSectionIndex > -1;
      const canNavigateRight = currentSectionIndex < allSections.length - 2;
      const canNavigateUp = currentAccountIndex > 0;
      const canNavigateDown = currentAccountIndex < allAccounts.length - 1;

      let newAccountIndex = currentAccountIndex;
      let newSectionIndex = currentSectionIndex;

      switch (direction) {
        case "left":
          if (canNavigateLeft) {
            newSectionIndex = currentSectionIndex - 1;
            if (newSectionIndex < -1) newSectionIndex = -1;
          }
          break;
        case "right":
          if (canNavigateRight) {
            newSectionIndex = currentSectionIndex + 1;
            const maxSectionIndex = allSections.length - 2;
            if (newSectionIndex > maxSectionIndex)
              newSectionIndex = maxSectionIndex;
          }
          break;
        case "up":
          if (canNavigateUp) newAccountIndex = currentAccountIndex - 1;
          break;
        case "down":
          if (canNavigateDown) newAccountIndex = currentAccountIndex + 1;
          break;
      }

      onNavigate(newAccountIndex, newSectionIndex);
    },
    [
      onNavigate,
      accountData,
      sectionId,
      currentAccountIndex,
      currentSectionIndex,
      allSections.length,
      allAccounts.length,
    ]
  );

  // Helper function to extract audio content for any section
  const getSectionAudioContent = React.useCallback(
    (
      sectionId: string,
      sectionData: any,
      orgName?: string,
      accountName?: string
    ): string | undefined => {
      if (!sectionData) return undefined;

      const sectionIdLower = sectionId.toLowerCase();
      switch (sectionIdLower) {
        case "overall":
          return extractOverallModalAudio(sectionData, orgName);
        case "financial":
          return extractFinancialModalAudio(sectionData, orgName);
        case "news":
          return extractNewsModalAudio(sectionData, orgName);
        case "social":
          return extractSocialModalAudio(sectionData, orgName);
        case "traffic":
          return extractTrafficModalAudio(sectionData, accountName);
        case "growth":
          return extractGrowthModalAudio(sectionData, orgName);
        case "legal":
          return extractLegalModalAudio(sectionData, orgName);
        case "about":
          return extractAboutModalAudio(sectionData, accountName, orgName);
        case "leadership":
        case "stack":
        default:
          // Use generic extractor for sections without specific modal extractors
          return extractSectionAudio(sectionIdLower, sectionData) || undefined;
      }
    },
    []
  );

  // Helper function to get section title
  const getSectionTitle = React.useCallback(
    (sectionId: string, accountName?: string): string => {
      const sectionIdLower = sectionId.toLowerCase();
      const sectionNames: Record<string, string> = {
        overall: "Overall Update",
        financial: "Financial Analysis",
        news: "News Analysis",
        social: "Social Media Analysis",
        traffic: "Digital Traffic Patterns",
        growth: "Growth Signals Analysis",
        legal: "Legal Analysis",
        about: "About Information",
        leadership: "Leadership & Org Changes",
        stack: "Technology Stack",
      };

      const sectionName =
        sectionNames[sectionIdLower] ||
        `${sectionId.charAt(0).toUpperCase() + sectionId.slice(1)} Analysis`;

      return `${accountName || "Account"}'s ${sectionName}`;
    },
    []
  );

  // Helper function to get section display name for navigation indicator
  const getSectionDisplayName = React.useCallback(
    (sectionId: string): string => {
      const sectionIdLower = sectionId.toLowerCase();
      const sectionNames: Record<string, string> = {
        overall: "Overall",
        financial: "Financial",
        news: "News",
        social: "Social",
        traffic: "Traffic",
        growth: "Growth",
        legal: "Legal",
        about: "About",
        leadership: "Leadership",
        stack: "Stack",
      };

      return (
        sectionNames[sectionIdLower] ||
        sectionId.charAt(0).toUpperCase() + sectionId.slice(1)
      );
    },
    []
  );

  // Audio toggle handler - opens Audio Agent Modal (must be before early returns)
  const handleToggleAudio = React.useCallback(
    (sectionId: string, audioContent?: string, accountName?: string) => {
      if (!audioContent) {
        console.warn("No audio content available for section:", sectionId);
        return;
      }

      const sectionTitle = getSectionTitle(sectionId, accountName);

      setAudioModalContent({
        title: sectionTitle,
        content: audioContent,
      });
      setIsAudioModalOpen(true);
    },
    [getSectionTitle]
  );

  // Keyboard navigation - must be before early returns
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handleNavigation("left");
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNavigation("right");
          break;
        case "ArrowUp":
          event.preventDefault();
          handleNavigation("up");
          break;
        case "ArrowDown":
          event.preventDefault();
          handleNavigation("down");
          break;
        case "Escape":
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    currentAccountIndex,
    currentSectionIndex,
    handleNavigation,
    onClose,
  ]);

  // Don't render if modal is closed or missing required data
  // This check must come AFTER all hooks
  if (!isOpen) return null;
  if (!accountData || !sectionId) return null;

  // Get section data
  const getSectionData = () => {
    if (!accountData.fullDigestData) return null;

    // Special case for "overall" section
    if (sectionId.toLowerCase() === "overall") {
      return (
        accountData.fullDigestData.summary?.overallAccountUpdate ||
        accountData.overallData ||
        null
      );
    }

    return accountData.fullDigestData[sectionId.toLowerCase()] || null;
  };

  const getSectionScoreData = () => {
    if (sectionId.toLowerCase() === "overall") {
      return accountData.overallData;
    }
    return accountData.sectionsData?.find(
      s => s.section_name === sectionId.toLowerCase()
    );
  };

  const sectionData = getSectionData();
  const sectionScoreData = getSectionScoreData();

  // Navigation handlers
  // Section index: -1 = overall, 0+ = regular sections
  const canNavigateLeft = currentSectionIndex > -1;
  const canNavigateRight = currentSectionIndex < allSections.length - 2; // -2 because -1 is overall, then 0+ are sections
  const canNavigateUp = currentAccountIndex > 0;
  const canNavigateDown = currentAccountIndex < allAccounts.length - 1;

  // Render section component based on sectionId
  const renderSection = () => {
    const orgName = organizationName || accountData.organizationName;

    // Extract audio content for any section
    const audioContent = getSectionAudioContent(
      sectionId,
      sectionData,
      orgName,
      accountData.accountName
    );

    const props = {
      data: sectionData,
      sectionData: sectionScoreData,
      accountName: accountData.accountName,
      organizationName: orgName,
      ...(audioContent && {
        audioContent,
        onToggleAudio: () =>
          handleToggleAudio(sectionId, audioContent, accountData.accountName),
      }),
    };

    switch (sectionId.toLowerCase()) {
      case "overall":
        return <OverallSection {...props} />;
      case "about":
        return <AboutSection {...props} />;
      case "financial":
        return <FinancialSection {...props} />;
      case "news":
        return <NewsSection {...props} />;
      case "leadership":
        return <LeadershipSection {...props} />;
      case "legal":
        return <LegalSection {...props} />;
      case "social":
        return <SocialSection {...props} />;
      case "traffic":
        return <TrafficSection {...props} />;
      case "growth":
        return <GrowthSection {...props} />;
      case "stack":
        return <StackSection {...props} />;
      case "summary":
        return <SummarySection {...props} />;
      default:
        return <div>Section &quot;{sectionId}&quot; not found</div>;
    }
  };

  return (
    <div
      className="section-detail-modal-overlay"
      onClick={onClose}
      style={{ display: isOpen ? "flex" : "none" }}
    >
      <div className="section-detail-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="section-modal-header">
          <div className="header-left">
            <h3>
              {accountData.accountName} -{" "}
              {sectionId.charAt(0).toUpperCase() + sectionId.slice(1)}
            </h3>
          </div>
          <div className="header-right">
            <button className="close-btn" onClick={onClose} title="Close">
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="modal-content">
          {sectionData ? (
            renderSection()
          ) : (
            <div className="no-data-message">
              <p>No data available for this section</p>
            </div>
          )}
        </div>

        {/* Bottom Navigation Bar */}
        {onNavigate && (
          <div className="modal-bottom-nav">
            <div className="nav-account-group">
              <button
                className="nav-btn-text"
                onClick={() => handleNavigation("up")}
                disabled={!canNavigateUp}
                title="Previous account"
              >
                ↑ Prev Account
              </button>
              <button
                className="nav-btn-text"
                onClick={() => handleNavigation("down")}
                disabled={!canNavigateDown}
                title="Next account"
              >
                ↓ Next Account
              </button>
            </div>
            <div className="nav-position-indicator">
              Account {currentAccountIndex + 1} of {allAccounts.length} •
              Section: {getSectionDisplayName(sectionId)}
            </div>
            <div className="nav-section-group">
              <button
                className="nav-btn-text"
                onClick={() => handleNavigation("left")}
                disabled={!canNavigateLeft}
                title="Previous section"
              >
                ← Prev Section
              </button>
              <button
                className="nav-btn-text"
                onClick={() => handleNavigation("right")}
                disabled={!canNavigateRight}
                title="Next section"
              >
                → Next Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Audio Agent Modal */}
      {audioModalContent && (
        <AudioAgentModal
          isOpen={isAudioModalOpen}
          onClose={() => {
            setIsAudioModalOpen(false);
            setAudioModalContent(null);
          }}
          title={audioModalContent.title}
          content={audioModalContent.content}
        />
      )}
    </div>
  );
};
